<template>
  <div ref="popoverContainer" class="d-inline popover-container" @mouseenter="onMouseenter" @mouseleave="onMouseleave">
    <div ref="triggerElement" :class="triggerClass" @click="onClick" @blur="onBlur" @focus="onFocus">
      <slot />
    </div>
    <Teleport to="body" :disabled="!teleport">
      <transition name="fade">
        <div
          v-show="visible"
          ref="popover"
          :class="['popover bs-popover-auto', popoverClass]"
          :style="popoverStyles"
          @mouseenter="onPopoverMouseenter"
          @mouseleave="onPopoverMouseleave"
        >
          <div v-if="title || $slots['title']" class="popover-header">
            <slot name="title">{{ title }}</slot>
          </div>
          <div class="popover-body">
            <div ref="arrow" class="arrow"></div>
            <slot name="content">{{ content }}</slot>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
import { computePosition, autoUpdate, offset, flip, shift, arrow } from "@floating-ui/dom";

export default {
  props: {
    placement: {
      type: String,
      default: "bottom",
    },
    content: {
      type: String,
      default: "",
    },
    title: {
      type: String,
      default: "",
    },
    trigger: {
      type: String,
      default: "hover",
    },
    triggerClass: {
      type: String,
      default: "d-inline",
    },
    popoverClass: {
      type: [String, Array, Object],
      default: "",
    },
    popoverStyle: {
      type: [String, Object],
      default: "",
    },
    delay: {
      type: Object,
      default: () => ({ show: 0, hide: 0 }),
    },
    gap: {
      type: Number,
      default: 20, // Adjusted for better alignment with Bootstrap
    },
    teleport: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["click", "mouseenter", "mouseleave", "blur", "focus"],
  data() {
    return {
      popoverStyles: {},
      arrowStyles: {},
      cleanupAutoUpdate: null,
      showTimeoutId: null,
      hideTimeoutId: null,
      visible: false,
    };
  },
  watch: {
    placement() {
      this.setTooltipPosition();
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.setTooltipPosition();
    });
    document.addEventListener("click", this.onClickOutside);
  },
  beforeUnmount() {
    this.cleanupAutoUpdate && this.cleanupAutoUpdate();
    document.removeEventListener("click", this.onClickOutside);
  },
  methods: {
    showTooltip() {
      clearTimeout(this.hideTimeoutId);
      this.showTimeoutId = setTimeout(() => {
        this.visible = true;
        this.setTooltipPosition();
      }, this.delay.show);
    },
    hideTooltip() {
      clearTimeout(this.showTimeoutId);
      this.hideTimeoutId = setTimeout(() => {
        this.visible = false;
        if (this.cleanupAutoUpdate) {
          this.cleanupAutoUpdate();
          this.cleanupAutoUpdate = null;
        }
      }, this.delay.hide);
    },
    onClick() {
      if (this.trigger === "click") {
        if (this.visible) {
          this.hideTooltip();
        } else {
          this.showTooltip();
        }
      }

      this.$emit("click");
    },
    onMouseenter() {
      if (this.trigger === "hover") {
        this.showTooltip();
      }
      this.$emit("mouseenter");
    },
    onMouseleave() {
      if (this.trigger === "hover") {
        this.hideTooltip();
      }
      this.$emit("mouseleave");
    },
    onPopoverMouseenter() {
      if (this.trigger === "hover") {
        clearTimeout(this.hideTimeoutId);
      }
    },
    onPopoverMouseleave() {
      if (this.trigger === "hover") {
        this.hideTooltip();
      }
    },
    onBlur() {
      if (this.trigger === "focus") {
        this.hideTooltip();
      }
      this.$emit("blur");
    },
    onFocus() {
      if (this.trigger === "focus") {
        this.showTooltip();
      }
      this.$emit("focus");
    },
    onClickOutside(e) {
      if (this.trigger === "click" && this.visible) {
        const container = this.$refs.popoverContainer;
        const popover = this.$refs.popover;
        const clickedInContainer = container && container.contains(e.target);
        const clickedInPopover = popover && popover.contains(e.target);
        if (!clickedInContainer && !clickedInPopover) {
          this.hideTooltip();
        }
      }
    },
    setTooltipPosition() {
      const referenceElement = this.$refs.triggerElement;
      const floatingElement = this.$refs.popover;
      const arrowElement = this.$refs.arrow;

      if (!referenceElement || !floatingElement || !arrowElement) return;

      this.cleanupAutoUpdate && this.cleanupAutoUpdate();

      const strategy = this.teleport ? "fixed" : "absolute";
      this.cleanupAutoUpdate = autoUpdate(referenceElement, floatingElement, () => {
        computePosition(referenceElement, floatingElement, {
          placement: this.placement,
          strategy: strategy,
          middleware: [offset(this.gap), flip(), shift({ padding: 10 }), arrow({ element: arrowElement, padding: 10 })],
        }).then(({ x, y, placement, middlewareData }) => {
          this.popoverStyles = {
            left: `${x}px`,
            top: `${y}px`,
            position: strategy,
            zIndex: 1060,
            ...(typeof this.popoverStyle === "object" ? this.popoverStyle : {}),
          };

          const { x: arrowX, y: arrowY } = middlewareData.arrow;

          const staticSide = {
            top: "bottom",
            right: "left",
            bottom: "top",
            left: "right",
          }[placement.split("-")[0]];

          // Remove any existing placement classes
          arrowElement.classList.remove("arrow-top", "arrow-right", "arrow-bottom", "arrow-left");

          // Add the new placement class
          arrowElement.classList.add(`arrow-${staticSide}`);

          Object.assign(arrowElement.style, {
            left: arrowX != null ? `${arrowX}px` : "",
            top: arrowY != null ? `${arrowY}px` : "",
            right: "",
            bottom: "",
          });
        });
      });
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s linear;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
